/** Anonymize… (plan "Actions menu" → Danger; O3): irreversible — the person's name, email
 *  and phone are scrubbed, the account deactivated and every session revoked. The operator
 *  types the user's email to confirm and gives a reason; the destructive button stays
 *  disabled until both are there. A failure stays in the dialog, in words. */
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useId, useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as usersApi from '@/api/users';

/** The shortest reason that says something. */
export const ANONYMIZE_REASON_MIN = 10;

export function AnonymizeDialog({
  user,
  onClose,
  onDone,
}: {
  user: Pick<usersApi.ConsoleUserDetail, 'id' | 'email'>;
  onClose: () => void;
  onDone: (email: string) => void;
}) {
  const titleId = useId();
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const confirmWith = user.email || String(user.id);
  const matches = typed.trim().toLowerCase() === confirmWith.toLowerCase();
  const reasonOk = reason.trim().length >= ANONYMIZE_REASON_MIN;
  const run = useMutation({
    mutationFn: () => usersApi.anonymize(user.id, reason.trim()),
    onSuccess: (r) => onDone(r.email),
    onError: (e: unknown) => setError(e instanceof ConsoleApiError ? e.message : 'Anonymize failed.'),
  });
  return (
    <Dialog open onClose={run.isPending ? undefined : onClose} aria-labelledby={titleId} maxWidth="sm" fullWidth>
      <DialogTitle id={titleId}>Anonymize this person?</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <WarningAmberIcon aria-hidden sx={{ color: 'error.main', mt: '2px' }} />
          <Typography variant="body2">
            <b>This cannot be undone.</b> The name, email and phone are scrubbed, the account is deactivated and every
            session revoked. Restore brings back the account state, never the erased data.
          </Typography>
        </Box>
        <TextField
          label={user.email ? 'Type the user’s email to confirm' : 'Type the user’s id to confirm'}
          placeholder={confirmWith}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          size="small"
          autoComplete="off"
          inputProps={{ spellCheck: false }}
          autoFocus
        />
        <TextField
          label="Reason"
          helperText={
            reasonOk ? 'Why the person’s data is erased (a request, a duplicate account, …)' : `At least ${ANONYMIZE_REASON_MIN} characters`
          }
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          size="small"
          multiline
          minRows={2}
          required
        />
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={run.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={!matches || !reasonOk || run.isPending}
          onClick={() => {
            setError(null);
            run.mutate();
          }}
        >
          {run.isPending ? 'Anonymizing…' : 'Anonymize'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
